/**
 * Client-side validation rules for uploaded data.
 * Returns { errors: [{row, col, message}], warnings: [{row, col, message}] }
 */

export function validateAddresses(addresses, workers, settings) {
  const errors = [];
  const warnings = [];
  const workerNames = new Set((workers || []).map(w => (w.name || '').toLowerCase()));

  addresses.forEach((addr, i) => {
    const row = i;

    // Missing address
    if (!addr.address && !addr.street) {
      errors.push({ row, col: 'address', message: 'Missing address' });
    }

    // Missing city
    if (!addr.city) {
      warnings.push({ row, col: 'city', message: 'Missing city' });
    }

    // Invalid lat/lon
    const lat = parseFloat(addr.lat);
    const lon = parseFloat(addr.lon);
    if (addr.lat && (isNaN(lat) || lat < -90 || lat > 90)) {
      errors.push({ row, col: 'lat', message: 'Invalid latitude' });
    }
    if (addr.lon && (isNaN(lon) || lon < -180 || lon > 180)) {
      errors.push({ row, col: 'lon', message: 'Invalid longitude' });
    }

    // Preferred tech references non-existent worker — blocking error per spec
    const prefTech = addr.preferred_technician || addr.pref_tech;
    if (prefTech && !workerNames.has(prefTech.toLowerCase())) {
      errors.push({ row, col: 'preferred_technician', message: `Tech "${prefTech}" not found in workers list` });
    }

    // Invalid required_days
    const reqDays = addr.required_days || addr.req_days;
    if (reqDays) {
      const validDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
      const days = String(reqDays).split(',').map(d => d.trim().toLowerCase());
      const invalid = days.filter(d => d && !validDays.includes(d));
      if (invalid.length > 0) {
        warnings.push({ row, col: 'required_days', message: `Invalid day(s): ${invalid.join(', ')}` });
      }
    }

    // Invalid qualification
    const minQual = addr.minimum_qualification || addr.min_qual;
    if (minQual) {
      const validQuals = ['st', 'lst', 'ss', 'standard', 'complex', 'both'];
      if (!validQuals.includes(minQual.toLowerCase())) {
        warnings.push({ row, col: 'minimum_qualification', message: `Invalid qualification: ${minQual}` });
      }
    }

    // Invalid service tier
    const serviceTier = addr.service_tier || addr.tier;
    if (serviceTier) {
      const validTiers = ['standard', 'complex', 'st', 'lst', 'ss'];
      if (!validTiers.includes(serviceTier.toLowerCase())) {
        warnings.push({ row, col: 'service_tier', message: `Unknown service tier: ${serviceTier}` });
      }
    }

    // Invalid per-customer service time
    const serviceTime = addr.service_time || addr.service_minutes;
    if (serviceTime) {
      const st = Number(serviceTime);
      if (isNaN(st) || st <= 0) {
        warnings.push({ row, col: 'service_time', message: 'Service time must be a positive number (minutes)' });
      }
    }
  });

  // Duplicate addresses (same address + city)
  const seen = new Map();
  addresses.forEach((addr, i) => {
    const key = `${(addr.address || '').toLowerCase()}|${(addr.city || '').toLowerCase()}`;
    if (key === '|') return;
    if (seen.has(key)) {
      warnings.push({ row: i, col: 'address', message: `Duplicate of row ${seen.get(key) + 1}` });
    } else {
      seen.set(key, i);
    }
  });

  // Duplicate coordinates (same lat/lon, different address text)
  const coordSeen = new Map();
  addresses.forEach((addr, i) => {
    const lat = parseFloat(addr.lat);
    const lon = parseFloat(addr.lon);
    if (!isNaN(lat) && !isNaN(lon)) {
      const coordKey = `${lat.toFixed(6)}|${lon.toFixed(6)}`;
      if (coordSeen.has(coordKey)) {
        warnings.push({ row: i, col: 'lat', message: `Same coordinates as row ${coordSeen.get(coordKey) + 1}` });
      } else {
        coordSeen.set(coordKey, i);
      }
    }
  });

  // Skill mismatch: complex customers but no complex-qualified techs
  const complexAddrs = addresses.filter(a => {
    const q = (a.minimum_qualification || a.min_qual || '').toLowerCase();
    return q === 'lst' || q === 'ss' || q === 'complex';
  });
  const hasComplexWorker = (workers || []).some(w => {
    const q = (w.qualification || w.qual || '').toLowerCase();
    return q === 'lst' || q === 'ss' || q === 'complex' || q === 'both';
  });
  if (complexAddrs.length > 0 && !hasComplexWorker) {
    errors.push({ row: -1, col: 'global', message: `${complexAddrs.length} addresses require higher qualification but no qualified workers exist` });
  }

  // Chem refill location not configured — spec requires mandatory daily chem stop
  if (settings && !settings.chem_refill_lat && !settings.chem_refill_lon) {
    errors.push({ row: -1, col: 'global', message: 'Chemical refill location not configured — go to Settings and set the refill address (mandatory daily stop)' });
  }

  // Capacity sanity check — estimate total daily workload vs shift hours
  if (settings && addresses.length > 0 && (workers || []).length > 0) {
    const workdays = (settings.workdays || ['Monday','Tuesday','Wednesday','Thursday','Friday']).length;
    const shiftSeconds = (settings.shift_hours || 8) * 3600;
    const serviceMinutes = settings.service_minutes || 20;
    const complexMinutes = settings.complex_service_minutes || serviceMinutes;
    const lunchMinutes = settings.lunch_break_duration_minutes || 0;
    const chemMinutes = settings.chem_refill_duration_minutes || 0;
    const numWorkers = (workers || []).length;

    // Estimate stops per tech per day
    const stopsPerDay = workdays > 0 ? Math.ceil(addresses.length / (numWorkers * workdays)) : 0;
    // Estimate service time per tech per day (mix of standard/complex)
    const complexCount = addresses.filter(a => {
      const q = (a.minimum_qualification || a.min_qual || '').toLowerCase();
      return q === 'lst' || q === 'ss' || q === 'complex';
    }).length;
    const complexRatio = addresses.length > 0 ? complexCount / addresses.length : 0;
    const avgServiceMin = serviceMinutes * (1 - complexRatio) + complexMinutes * complexRatio;
    const servicePerDay = stopsPerDay * avgServiceMin;
    // Assume ~30 min driving overhead per tech per day (conservative)
    const driveEstimate = 30 * stopsPerDay / Math.max(stopsPerDay, 1);
    const totalDailyMinutes = servicePerDay + driveEstimate + lunchMinutes + chemMinutes;
    const shiftMinutes = shiftSeconds / 60;

    if (totalDailyMinutes > shiftMinutes * 1.1) {
      warnings.push({
        row: -1, col: 'global',
        message: `Estimated daily workload (~${Math.round(totalDailyMinutes)} min/tech) may exceed shift limit (${Math.round(shiftMinutes)} min) — consider adding technicians or reducing stops`
      });
    }
  }

  return { errors, warnings };
}

export function validateWorkers(workers) {
  const errors = [];
  const warnings = [];

  const validDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  workers.forEach((w, i) => {
    if (!w.name) {
      errors.push({ row: i, col: 'name', message: 'Missing worker name' });
    }
    const qual = (w.qualification || w.qual || '').toLowerCase();
    if (qual && !['st', 'lst', 'ss', 'standard', 'complex', 'both'].includes(qual)) {
      warnings.push({ row: i, col: 'qualification', message: `Unknown qualification: ${qual}` });
    }

    // Home address required when starting from home
    const startLoc = (w.start_location || '').toLowerCase();
    if (startLoc === 'home' && !w.home_address) {
      warnings.push({ row: i, col: 'home_address', message: 'Start location is Home but no home address provided' });
    }

    // Days available validation
    const daysAvail = w.days_available;
    if (daysAvail) {
      const days = String(daysAvail).split(',').map(d => d.trim().toLowerCase()).filter(Boolean);
      const invalid = days.filter(d => !validDays.includes(d));
      if (invalid.length > 0) {
        warnings.push({ row: i, col: 'days_available', message: `Invalid day(s): ${invalid.join(', ')}` });
      }
    }
  });

  // Duplicate names
  const names = new Map();
  workers.forEach((w, i) => {
    const n = (w.name || '').toLowerCase();
    if (!n) return;
    if (names.has(n)) {
      warnings.push({ row: i, col: 'name', message: `Duplicate of row ${names.get(n) + 1}` });
    } else {
      names.set(n, i);
    }
  });

  return { errors, warnings };
}
